import React, { Component } from "react"
import PropTypes from "prop-types"

import { ADDRESS_KEY } from "shared-web/forms/constants"

export class AddressAutocompleteInput extends Component {
    static defaultProps = {
        inputProps: {
            value: PropTypes.string.isRequired,
            onChange: PropTypes.func.isRequired,
        },
        onAddressClick: PropTypes.func.isRequired,
        options: {},
        shouldGeocode: false,
        addressKey: "input_address",
    }

    state = {
        hasInitializedGeocoding: false,
    }

    liveRegionId = `address-live-${Math.random().toString(36).substr(2, 9)}`

    handleBlur = () => {
        const liveRegion = document.getElementById(this.liveRegionId)
        if (liveRegion) {
            liveRegion.textContent = ""
        }
    }

    componentDidMount() {
        // in some cases, the google maps script may fail to load
        if (!window.google) {
            setTimeout(() => {
                // try once more if first timeout is not enough
                if (!window.google) {
                    setTimeout(() => {
                        // if both waits are not long enough, just give up on the autocomplete
                        if (window.google) {
                            this.init()
                        }
                    }, 15000)
                } else {
                    this.init()
                }
            }, 5000)
        } else {
            this.init()
        }
    }

    init = () => {
        // one day we might want more fields. For now, we're not going to bother.
        // If we ever need the priciness of restaurants according to Google,
        // please contact me to tell me.
        const fields = this.props.shouldGeocode
            ? ["address_components", "geometry", "place_id", "formatted_address"]
            : ["place_id", "name", "types"]

        const options = {
            ...this.props.options,
            fields,
            types: ["address"],
        }

        this.addressTypeahead = new google.maps.places.Autocomplete(this.addressInput, options)

        this.addressTypeahead.addListener("place_changed", this.onAddressSelect)

        // Set up accessibility attributes
        this.setupAccessibility()

        this.setState({ hasInitializedGeocoding: true })
    }

    setupAccessibility = () => {
        if (!this.addressInput) return

        // Set ARIA attributes on the input
        this.addressInput.setAttribute("role", "combobox")
        this.addressInput.setAttribute("aria-autocomplete", "list")
        this.addressInput.setAttribute("aria-expanded", "false")
        this.addressInput.setAttribute("aria-haspopup", "listbox")

        // Find Google's pac-container and give it an ID so we can reference it
        // The pac-container is created by Google after initialization
        setTimeout(() => {
            const pacContainer = document.querySelector(".pac-container")
            if (pacContainer && !pacContainer.id) {
                const dropdownId = `pac-container-${Math.random().toString(36).substr(2, 9)}`
                pacContainer.id = dropdownId
                this.addressInput.setAttribute("aria-controls", dropdownId)
            }
        }, 100)

        // Clear announcement when field loses focus
        this.addressInput.addEventListener("blur", this.handleBlur)

        // Watch for when the dropdown appears/disappears
        this.observeDropdown()
    }

    observeDropdown = () => {
        let previousVisibility = false
        let clearTimer = null

        const observer = new MutationObserver(() => {
            const pacContainer = document.querySelector(".pac-container")
            if (pacContainer) {
                const isVisible = pacContainer.style.display !== "none" && pacContainer.offsetParent !== null

                if (isVisible !== previousVisibility) {
                    this.addressInput.setAttribute("aria-expanded", isVisible ? "true" : "false")

                    const liveRegion = document.getElementById(this.liveRegionId)
                    if (liveRegion) {
                        if (clearTimer) {
                            clearTimeout(clearTimer)
                            clearTimer = null
                        }

                        if (isVisible) {
                            liveRegion.textContent = "Autocomplete suggestions available"

                            // Auto-clear after 1 second so next announcement is detected as a change
                            clearTimer = setTimeout(() => {
                                liveRegion.textContent = ""
                                clearTimer = null
                            }, 1000)
                        } else {
                            liveRegion.textContent = ""
                        }
                    }

                    previousVisibility = isVisible
                }
            }
        })

        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["style", "class"],
        })

        this.dropdownObserver = observer
    }

    componentWillUnmount() {
        if (this.dropdownObserver) {
            this.dropdownObserver.disconnect()
        }
        if (this.addressInput) {
            this.addressInput.removeEventListener("blur", this.handleBlur)
        }
    }

    onAddressSelect = () => {
        // if getPlace is called with the default fields ['place_id', 'name', 'types],
        // a geocoding request will NOT be made. However, if you
        // get more specific fields such as address_components,
        // geometry, or anything, a getPlaceDetail WILL be called
        // and we will be charged money. So if we're getting that
        // information, be sure to use it!
        //
        // If you're geocoding, this will set the value on the redux form field itself,
        // so your submit form will have to take care of that.
        const { address_components, geometry, place_id, formatted_address } = this.addressTypeahead.getPlace()

        let formValue = this.props.shouldGeocode
            ? {
                  address_components,
                  geometry,
                  place_id: place_id,
                  formatted_address: formatted_address,
                  [ADDRESS_KEY]: this.addressInput.value,
              }
            : this.addressInput.value

        this.props.onAddressClick(formValue)
    }

    render() {
        return (
            <>
                <input
                    ref={(input) => (this.addressInput = input)}
                    {...this.props.inputProps}
                    style={{ borderRadius: "4px" }}
                    aria-describedby={this.liveRegionId}
                />
                <div
                    id={this.liveRegionId}
                    role="status"
                    aria-live="assertive"
                    aria-atomic="true"
                    style={{
                        position: "absolute",
                        left: "-10000px",
                        width: "1px",
                        height: "1px",
                        overflow: "hidden",
                    }}
                />
            </>
        )
    }
}

export default AddressAutocompleteInput
